/**
 * Promo + newsletter popups.
 *
 * Ports app/components/global/popups/SettingsPopup.vue and
 * app/components/global/popups/NewsletterPopup.vue. Both render as sections
 * of the `overlay` section group (sections/popup-promo.liquid,
 * sections/popup-newsletter.liquid) and start `hidden` in markup; this file
 * owns everything dynamic: cookies, delayed show, close controls, the
 * theme-editor select/deselect behaviour, and the ordering between the two.
 *
 * Outside the theme editor:
 *   - The promo popup shows after a 2s delay, unless `popupSettingsClosed`
 *     is already set (or the section isn't rendered at all, i.e. disabled).
 *   - The newsletter popup shows after its own 2s delay, unless
 *     `popupNewsletterClosed` is already set — but if the promo popup is
 *     enabled and hasn't been closed yet, it waits for the promo popup's
 *     close (the `figursky:popup:closed` event) before starting that delay,
 *     so the two never show together.
 *   - Closing either popup (button, overlay click for the promo popup, or
 *     Escape) sets its cookie (path `/`, 30 days, SameSite=Lax) so it does
 *     not show again on this browser.
 *
 * Inside the theme editor (window.Shopify.designMode):
 *   - No timers and no cookies. A popup opens the instant its section is
 *     selected (`shopify:section:select`) and closes on
 *     `shopify:section:deselect`, so merchants can preview it on demand.
 *   - The cookie is never written in design mode, however a popup is
 *     dismissed — deselecting the section, or using its own close
 *     button/overlay/Escape all pass `skipCookie: true` (via isDesignMode()),
 *     so previewing a popup never affects a real visitor's cookies and never
 *     fires the `figursky:popup:closed` ordering event.
 *
 * Both popups are modal dialogs: opening one traps Tab inside it and closing
 * it hands focus back, through the shared window.figursky.focus helper in
 * assets/theme.js.
 */
(function () {
  'use strict';

  var COOKIE_NAMES = {
    promo: 'popupSettingsClosed',
    newsletter: 'popupNewsletterClosed',
  };
  var COOKIE_VALUE = 'closed';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
  var SHOW_DELAY = 2000;
  // `hidden` is only set once the fade-out has finished, so the popup is not
  // yanked out mid-transition: 0.2s for the newsletter popup
  // (_popup-newsletter.scss, ported from NewsletterPopup.vue's slideOut) and
  // 0.3s for the promo popup (_popup-promo.scss). Under
  // `prefers-reduced-motion: reduce` neither fades, so there is nothing to wait
  // for and the popup is hidden at once.
  var HIDE_DELAY = 300;

  function hideDelay() {
    var animates = window.matchMedia && window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    return animates ? HIDE_DELAY : 0;
  }
  var CLOSED_EVENT = 'figursky:popup:closed';

  /* ---------------------------------------------------------------- cookies */

  function getCookie(name) {
    var pattern = new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)');
    var match = document.cookie.match(pattern);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + COOKIE_MAX_AGE + '; SameSite=Lax';
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* ----------------------------------------------------------------- popups */

  function getPopupEl(id) {
    return document.querySelector('[data-popup="' + id + '"]');
  }

  function isOpen(el) {
    return !!el && el.classList.contains('is-open');
  }

  /* Shared with the drawers — see the focus section of assets/theme.js. */
  function focusHelper() {
    return window.figursky && window.figursky.focus;
  }

  function trapFocus(el) {
    var focus = focusHelper();
    if (focus && typeof focus.trap === 'function') focus.trap(el);
  }

  function releaseFocus(el) {
    var focus = focusHelper();
    if (focus && typeof focus.release === 'function') focus.release(el);
  }

  function openPopup(id) {
    var el = getPopupEl(id);
    if (!el || isOpen(el)) return;

    el.hidden = false;
    // Force a reflow so the browser registers the un-hidden state before the
    // `is-open` class is added, otherwise the CSS transition never runs.
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.classList.add('is-open');

    if (id === 'promo' && window.figursky && typeof window.figursky.lockScroll === 'function') {
      window.figursky.lockScroll();
    }

    trapFocus(el);
  }

  function closePopup(id, options) {
    var el = getPopupEl(id);
    if (!el) return;

    el.classList.remove('is-open');
    releaseFocus(el);

    if (id === 'promo' && window.figursky && typeof window.figursky.unlockScroll === 'function') {
      window.figursky.unlockScroll();
    }

    window.setTimeout(function () {
      if (!isOpen(el)) el.hidden = true;
    }, hideDelay());

    // Every design-mode close path (deselect, or the popup's own close
    // button/overlay/Escape) passes skipCookie so previewing a popup never
    // writes the cookie or fires the close event that unblocks the
    // newsletter popup's wait (see scheduleProduction) — that ordering only
    // applies to a real, cookie-worthy close outside the editor.
    if (!(options && options.skipCookie)) {
      setCookie(COOKIE_NAMES[id], COOKIE_VALUE);
      document.dispatchEvent(new CustomEvent(CLOSED_EVENT, { detail: { id: id } }));
    }
  }

  /* -------------------------------------------------------- close controls */

  // These are wired unconditionally (not just inside scheduleProduction), so
  // a merchant in the theme editor can dismiss an open popup with its own
  // close button/overlay/Escape instead of deselecting the section. Every
  // call here must still honour "ignore the cookie in design mode" — hence
  // the isDesignMode() check on every closePopup() below — otherwise that
  // path would write the cookie and fire the ordering event just like a real
  // close.

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    // SettingsPopup.vue closed on the CTA click as well as on the close
    // button. No preventDefault: the link still navigates, the cookie is set
    // first so the popup does not greet the visitor again on the next page.
    var cta = target.closest('[data-popup="promo"] .settings-popup__cta a');
    if (cta) {
      closePopup('promo', { skipCookie: isDesignMode() });
      return;
    }

    var closeButton = target.closest('[data-popup-close]');
    if (closeButton) {
      event.preventDefault();
      closePopup(closeButton.getAttribute('data-popup-close'), { skipCookie: isDesignMode() });
      return;
    }

    var overlay = target.closest('[data-popup-overlay]');
    if (overlay) {
      closePopup(overlay.getAttribute('data-popup-overlay'), { skipCookie: isDesignMode() });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    ['promo', 'newsletter'].forEach(function (id) {
      if (isOpen(getPopupEl(id))) closePopup(id, { skipCookie: isDesignMode() });
    });
  });

  /* -------------------------------------------------------------- schedule */

  function scheduleProduction() {
    var promoEl = getPopupEl('promo');
    var newsletterEl = getPopupEl('newsletter');

    var promoWillShow = !!promoEl && getCookie(COOKIE_NAMES.promo) !== COOKIE_VALUE;

    if (promoWillShow) {
      window.setTimeout(function () {
        openPopup('promo');
      }, SHOW_DELAY);
    }

    if (newsletterEl && getCookie(COOKIE_NAMES.newsletter) !== COOKIE_VALUE) {
      if (promoWillShow) {
        var onPromoClosed = function (event) {
          if (event.detail && event.detail.id === 'promo') {
            document.removeEventListener(CLOSED_EVENT, onPromoClosed);
            window.setTimeout(function () {
              openPopup('newsletter');
            }, SHOW_DELAY);
          }
        };
        document.addEventListener(CLOSED_EVENT, onPromoClosed);
      } else {
        window.setTimeout(function () {
          openPopup('newsletter');
        }, SHOW_DELAY);
      }
    }
  }

  function scheduleDesignMode() {
    // The section the merchant currently has selected in the editor sidebar.
    var selectedSectionId = null;

    function popupTypeForSection(id) {
      var types = ['promo', 'newsletter'];
      for (var i = 0; i < types.length; i++) {
        var el = getPopupEl(types[i]);
        if (el && el.getAttribute('data-popup-section-id') === id) return types[i];
      }
      return null;
    }

    document.addEventListener('shopify:section:select', function (event) {
      var id = event.detail && event.detail.sectionId;
      selectedSectionId = id;
      var type = popupTypeForSection(id);
      if (type) openPopup(type);
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      var id = event.detail && event.detail.sectionId;
      if (selectedSectionId === id) selectedSectionId = null;
      var type = popupTypeForSection(id);
      if (type) closePopup(type, { skipCookie: true });
    });

    // Editing any setting re-renders the section, and the fresh markup starts
    // `hidden` — which used to make the popup vanish mid-edit. Re-open it if
    // its section is still the selected one.
    document.addEventListener('shopify:section:load', function (event) {
      var id = event.detail && event.detail.sectionId;
      if (id !== selectedSectionId) return;
      var type = popupTypeForSection(id);
      if (type) openPopup(type);
    });
  }

  function boot() {
    if (isDesignMode()) {
      scheduleDesignMode();
    } else {
      scheduleProduction();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
